import { theme, useMediaQuery } from '@chakra-ui/react';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

import { MobileTable } from './mobile-table';
import { TableBase } from './table-base';
import { TableBaseProps, TableFilter, TableInitialState, TablePagination, TableSortValue } from './types';
import { filterUnmarshalling } from './utils';

type TableProps<I extends EntityBase = EntityBase> = Pick<TableBaseProps<I>, 
  'columns' | 'title' | 'disabled' | 'onCreateClick' | 'onDownload' | 'data' | 'keyProperty' | 'paper' | 'loading' | 'createMenu' | 'additionalToolbar' | 'onFilterChange' | 'footerAdditional' | 'getRowBackgroundColor' > & TableInitialState<I> & {
};

export function Table<I extends EntityBase = EntityBase>(props: TableProps<I>) {
  const {
    data,
    initialFilter,
    initialSort,
    columns,
    disabled,
    onFilterChange,
    ...restProps
  } = props;

  const [pagination, setPagination] = useState<TablePagination>({
    page: 1,
    pageSize: 10,
  });

  const [sort, setSort] = useState<TableSortValue<I>>(initialSort || {
    name: 'id',
    direction: 'asc',
  });

  const [filter, setFilter] = useState<TableFilter<I>[]>(filterUnmarshalling<I>({}, columns));

  const onSetFilter = useCallback((filter: TableFilter<I>[]) => {
    setFilter(filter);
    onFilterChange?.(filter);
  }, [onFilterChange]);

  const featuredData = useMemo(() => {
    // filter
    const filteredData = data.filter((item) => {
      return filter.every(({ name, type, value, isApplied }) => {
        if (!value || value.length === 0 || isApplied) {return true;}
        switch (type) {
          case 'autocomplete': return item[name]?.id === (value as EntityBase)?.id;
          case 'select': return item[name]?.id === (value as EntityBase)?.id;
          case 'multi-select': return (value as EntityBase[]).some(({id}) => id === item[name]?.id);
          case 'text': return value == item[name];
          case 'date': return moment(value as string).isSame(item[name]);
          case 'date-range': return moment(value as string).isSame(item[name]);
          case 'date-time': return moment(value as string).isSame(item[name]);
          default: return false;
        }
      });
    });
    // sort
    const { name, direction } = sort;
    filteredData.sort((itemA, itemB) => {
      const directionSign = direction === 'asc' ? 1 : -1;
      if (!Number.isNaN(Number(itemA[name])) && !Number.isNaN(Number(itemB[name]))) {
        return Number(itemA[name]) > Number(itemB[name]) ? directionSign : -directionSign;
      }
      if (typeof itemA[name] === 'string') {
        return itemA[name].localeCompare(itemB[name]) * directionSign;
      }
      if (typeof itemA[name] === 'object') {
        return itemA[name]?.name?.localeCompare(itemB[name]?.name) * directionSign;
      }
    });
    // page 
    const { page, pageSize } = pagination;
    return filteredData.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
  }, [data, pagination, filter, sort]);

  const [isDesktop] = useMediaQuery('(min-width: 768px)', {
    fallback: true
  });
  
  if (!isDesktop) {
    return <MobileTable
      data={featuredData}
      total={data?.length || 0}
      sort={sort}
      filter={filter}
      pagination={pagination}
      onFilterChange={onSetFilter}
      onSortChange={setSort}
      onPaginationChange={setPagination}
      columns={columns}
      {...restProps}
    />;
  }

  return <TableBase<I> 
    data={featuredData}
    total={data?.length || 0}
    sort={sort}
    filter={filter}
    disabled={disabled}
    pagination={pagination}
    onFilterChange={onSetFilter}
    onSortChange={setSort}
    onPaginationChange={setPagination}
    columns={columns}
    {...restProps}
  />;
}